import { LitElement, html } from 'lit';
import { customElement, query, state, property } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import { IGif } from '@giphy/js-types';
import { renderGif } from '@giphy/js-components';

import { emit } from '@amityeko/pbc-core/dist/utilities/event';
import { setSmoothLoading } from '@amityeko/pbc-core/dist/utilities/loading';
import { withAuth } from '@amityeko/pbc-core/dist/providers/auth/consumer-mixin';

import closeIcon from '@amityeko/design-system/dist/assets/icons/close';

import '@amityeko/design-system/dist/elements/typography/tg-caption';
import '@amityeko/design-system/dist/elements/typography/tg-label-two';
import '@amityeko/design-system/dist/elements/icon';
import '@amityeko/design-system/dist/elements/input';
import '@amityeko/design-system/dist/elements/video';
import '@amityeko/design-system/dist/elements/button';
import '@amityeko/design-system/dist/elements/dialog';
import '@amityeko/design-system/dist/elements/textarea';
import '@amityeko/design-system/dist/elements/image-editor';
import '@amityeko/design-system/dist/elements/image-selector';
import '@amityeko/design-system/dist/elements/color-selector';
import '@amityeko/design-system/dist/elements/chips-selection';

import type AmityekoInput from '@amityeko/design-system/dist/elements/input';
import type AmityekoDialog from '@amityeko/design-system/dist/elements/dialog';
import type AmityekoTextarea from '@amityeko/design-system/dist/elements/textarea';
import AmityekoImageEditor from '@amityeko/design-system/dist/elements/image-editor';
import type AmityekoImageSelector from '@amityeko/design-system/dist/elements/image-selector';
import type AmityekoColorSelector from '@amityeko/design-system/dist/elements/color-selector';
import type { ImageSelectorAsset } from '@amityeko/design-system/dist/elements/image-selector/types';

import '../audio';
import '../record-audio';
import '../record-video';
import '../image-layout-content';

import { Images } from '../image-layout/types';
import { BadgeList } from '../badges-dashboard/types';
import type RnRRecordAudio from '../record-audio';
import RnRRecordVideo from '../record-video';
import type RnRImageLayoutContent from '../image-layout-content';

import './gif-selector-dialog';
import type RnRGifSelectorDialog from './gif-selector-dialog';

import { mockGradientData, colorData, chipList, RECOGNITION_TYPE } from './constants';

import styles from './styles';

/**
 * @slot default - Element that triggers the dialog with create recognition
 */
@customElement('rnr-create-recognition')
export default class RnRCreateRecognition extends withAuth(LitElement) {
  static styles = styles;

  @query('#create_recognition_dialog') createRecognitionDialog!: AmityekoDialog;
  @query('#discard_changes_dialog') discardChangesDialog!: AmityekoDialog;
  @query('#gif_selector') gifSelectorDialog!: RnRGifSelectorDialog;
  @query('#image_editor') imageEditorDialog!: AmityekoImageEditor;
  @query('#record_audio') recordAudioDialog!: RnRRecordAudio;
  @query('#record_video') recordVideoDialog!: RnRRecordVideo;

  @query('#textarea_message') messageElem!: AmityekoTextarea;
  @query('#image_selector_background') backgroundImgElem!: AmityekoImageSelector;
  @query('#color_selector_text') textColorElem!: AmityekoColorSelector;
  @query('#image_layout_content') imageLayoutContentElem!: RnRImageLayoutContent;
  @query('#gif') gifElem!: HTMLDivElement;

  @query('#video_title') videoTitle!: AmityekoInput;
  @query('#video_title_color_selector') videoTitleColorElem!: AmityekoColorSelector;
  @query('#discard_video_dialog') discardVideoDialog!: AmityekoDialog;

  @query('#audio_title') audioTitle!: AmityekoInput;
  @query('#audio_image_selector_background') audioBackgroundImgElem!: AmityekoImageSelector;
  @query('#audio_title_color_selector') audioTitleColorElem!: AmityekoColorSelector;
  @query('#audio_image_layout_content') audioImageLayoutContentElem!: RnRImageLayoutContent;
  @query('#audio_gif') audioGifElem!: HTMLDivElement;

  @state()
  badge?: BadgeList;

  @state()
  backgroundList?: ImageSelectorAsset[];

  @state()
  images?: Images;

  @state()
  type: string = chipList[0].id;

  @state()
  videoURL?: string;

  @state()
  videoData?: Blob;

  @state()
  newType?: string;

  @state()
  gif?: IGif;

  @state()
  audioURL?: string;

  @state()
  isSafari: boolean = false;

  @state()
  badgeLimit?: number;

  @state()
  footerInfoText?: string;

  @state()
  isLimitationLoading: boolean = false;

  public firstUpdated() {
    this.backgroundList = mockGradientData;
    this.isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }

  public show(badge: any = {}, images: any = {}) {
    this.createRecognitionDialog.show();
    if (Object.keys(badge).length > 0) {
      this.badge = badge;
      this._getBadgeLimitation(badge?.id);
    }
    if (Object.keys(images).length > 0) {
      this.images = images;
    }
  }

  reset() {
    switch (this.type) {
      case RECOGNITION_TYPE.TEXT:
        this.gif = undefined;
        this.messageElem?.reset();
        this.textColorElem?.reset();
        this.imageLayoutContentElem?.reset();
        this.images = undefined;
        this.backgroundImgElem.reset();
        break;
      case RECOGNITION_TYPE.VIDEO:
        this.videoTitle?.reset();
        this.videoTitleColorElem?.reset();
        this.videoURL = undefined;
        break;
      case RECOGNITION_TYPE.AUDIO:
        //TODO: to add new field for audio
        this.audioTitle?.reset();
        this.audioTitleColorElem?.reset();
        this.audioBackgroundImgElem?.reset();
        this.gif = undefined;
        this.images = undefined;
        this.audioURL = undefined;
        this.audioImageLayoutContentElem.reset();
        break;
      default:
        break;
    }
  }

  private _onTriggerClick() {
    this.createRecognitionDialog.show();
  }

  private _onDialogBack() {
    this.createRecognitionDialog.hide();
    this.reset();
    emit(this, 'rnr-create-recognition-dialog-back');
  }

  private async _getBadgeLimitation(badgeId: string) {
    try {
      //call badge limitation api
      const response = await this.auth?.http?.get(
        `/api/v1/badges/${badgeId}/limitations/remaining`,
      );
      this.badgeLimit = response?.remainingRecognitions;

      if (this.badgeLimit === 0) {
        this.footerInfoText =
          'You have exhausted the limit to send this badge. Please contact admin or select other badges';
        //@ts-ignore
      } else if (this.badgeLimit > 0) {
        //@ts-ignore
        const text = this.badgeLimit > 1 ? 'recognitions' : 'recognition';
        this.footerInfoText = `You have ${this.badgeLimit} ${this.badge?.cardTitle} ${text} to send.`;
      } else {
        this.footerInfoText = undefined;
      }

      setSmoothLoading(1000).then((loadingState: boolean) => {
        this.isLimitationLoading = loadingState;
      });
    } catch (e: any) {
      setSmoothLoading(1000).then((loadingState: boolean) => {
        this.isLimitationLoading = loadingState;
      });
      emit(this, 'amityeko-notification--toast', {
        detail: { message: e?.error?.message, variant: 'error' },
      });
    }
  }

  //related methods with on select type
  private _onSelectType(e: CustomEvent) {
    this.newType = e?.detail.id;
    switch (this.type) {
      case RECOGNITION_TYPE.TEXT:
        if (
          this.messageElem?.value ||
          this.images ||
          this.gif ||
          (this.backgroundImgElem?.selectedId &&
            this.backgroundImgElem?.selectedId !== mockGradientData[0].id) ||
          (this.textColorElem?.selectedColor && this.textColorElem?.selectedColor !== colorData[0])
        ) {
          this.createRecognitionDialog.hide();
          this.discardChangesDialog.show();
        } else {
          this.type = e?.detail?.id;
        }
        break;
      case RECOGNITION_TYPE.VIDEO:
        if (
          this.videoTitle?.value ||
          this.videoURL ||
          (this.videoTitleColorElem?.selectedColor &&
            this.videoTitleColorElem?.selectedColor !== colorData[0])
        ) {
          this.createRecognitionDialog.hide();
          this.discardChangesDialog.show();
        } else {
          this.type = e?.detail?.id;
        }
        break;
      case RECOGNITION_TYPE.AUDIO:
        //TODO: to add new field for audio
        if (
          this.audioTitle?.value ||
          this.images ||
          this.gif ||
          this.audioURL ||
          (this.audioBackgroundImgElem?.selectedId &&
            this.audioBackgroundImgElem?.selectedId !== mockGradientData[0].id) ||
          (this.audioTitleColorElem?.selectedColor &&
            this.audioTitleColorElem?.selectedColor !== colorData[0])
        ) {
          this.createRecognitionDialog.hide();
          this.discardChangesDialog.show();
        } else {
          this.type = e?.detail?.id;
        }
        break;
      default:
        break;
    }
  }

  private _onContinueEditing() {
    this.discardChangesDialog.hide();
    this.createRecognitionDialog.show();
  }

  private _onDiscardChanges() {
    this.reset();
    if (this.newType) {
      this.type = this.newType;
    }
    this.discardChangesDialog.hide();
    this.createRecognitionDialog.show();
  }

  //realated method with background image
  private _handleFile(e: any) {
    if (e.target.files.length) {
      this.createRecognitionDialog.hide();
      //@ts-ignore
      this.imageEditorDialog.show(e);
      e.target.value = '';
    }
  }

  private _handleBackgroundImage(e: CustomEvent) {
    this.createRecognitionDialog.show();
    const imageURL = e.detail.imageData.toDataURL();
    e?.detail.imageData.toBlob(async (blob: any) => {
      const formData = new FormData();
      const fileName = `${e?.detail?.fileName.split('.')[0]}.png`;
      formData.append('file', blob, fileName);

      const response = await this.auth?.http?.upload('/api/v1/media/images', formData);
      if (this.backgroundList) {
        this.backgroundList = [
          ...this.backgroundList,
          {
            type: 'image',
            id: response[0]?.key,
            src: imageURL,
          },
        ];
        if (this.type === 'audio') {
          this.audioBackgroundImgElem.selectedId = response[0]?.key;
        }
        {
          this.backgroundImgElem.selectedId = response[0]?.key;
        }
      }
    });
  }

  //related methods with gif
  private _onClickAddGifButton() {
    this.createRecognitionDialog.hide();
    this.gifSelectorDialog.show();
  }

  private _onBackGifSelector() {
    this.createRecognitionDialog.show();
  }

  private _onSelectGif(e: CustomEvent) {
    this.gif = e.detail?.gif;
    const gifElem = this.gifElem || this.audioGifElem;
    if (this.gif) {
      renderGif({ gif: this.gif, width: 128, hideAttribution: true, noLink: true }, gifElem);
    }
    this.createRecognitionDialog.show();
  }

  //related methods with image
  private _handleUploadImage() {
    this.createRecognitionDialog.hide();
    emit(this, 'rnr-click-upload-image');
  }

  private _onClickResetVisualButton() {
    if (this.images) {
      this.images = undefined;
      this.imageLayoutContentElem && this.imageLayoutContentElem.reset();
      this.audioImageLayoutContentElem && this.audioImageLayoutContentElem.reset();
      emit(this, 'rnr-click-upload-image-cancel');
    }

    if (this.gif) {
      this.gif = undefined;
    }
  }

  //related methods with video
  private _handleRecordVideo() {
    this.createRecognitionDialog.hide();
    this.recordVideoDialog.show();
  }

  private _onVideoRecordStop(e: CustomEvent) {
    this.videoData = e?.detail?.video;
    this.videoURL = URL.createObjectURL(e?.detail?.video);
    this.createRecognitionDialog.show();
  }

  private _onVideoRecordBack() {
    this.createRecognitionDialog.show();
  }

  private _handleUploadVideo(e: any) {
    this.videoData = e.target.files[0];
    if (e.target.files[0]) {
      this.videoURL = URL.createObjectURL(e.target.files[0]);
    }
  }

  private _onContinueVideo() {
    this.discardVideoDialog.hide();
    this.createRecognitionDialog.show();
  }

  private _onDiscardVideo() {
    this.videoURL = undefined;
    this.videoData = undefined;
    this.discardVideoDialog.hide();
    this.createRecognitionDialog.show();
  }

  //related methods with audio
  private _handleRecordAudio() {
    this.createRecognitionDialog.hide();
    this.recordAudioDialog.show();
  }

  private _onAudioRecordStop(e: CustomEvent) {
    this.audioURL = URL.createObjectURL(e?.detail?.audio);
    this.createRecognitionDialog.show();
  }

  private _onAudioRecordBack() {
    this.createRecognitionDialog.show();
  }

  private _handleUploadAudio(e: any) {
    if (e.target.files[0]) {
      this.audioURL = URL.createObjectURL(e.target.files[0]);
    }
  }

  //related methods with form validate, submit and reset
  private _checkFormValidity() {
    let validityFlags;
    switch (this.type) {
      case RECOGNITION_TYPE.TEXT:
        validityFlags = [
          this.messageElem.textarea?.reportValidity(),
          Boolean(this.backgroundImgElem?.selectedId),
          Boolean(this.textColorElem?.selectedColor),
        ];
        break;
      case RECOGNITION_TYPE.VIDEO:
        validityFlags = [
          this.videoTitle.input?.reportValidity(),
          Boolean(this.videoTitleColorElem?.selectedColor),
          Boolean(this.videoURL),
        ];
        break;
      case RECOGNITION_TYPE.AUDIO:
        //TODO: to add new field for audio
        validityFlags = [
          this.audioTitle.input?.reportValidity(),
          Boolean(this.audioTitleColorElem?.selectedColor),
          Boolean(this.audioBackgroundImgElem?.selectedId),
          Boolean(this.audioURL),
        ];

        break;
      default:
        break;
    }
    return validityFlags?.every(flag => flag);
  }

  private async _onConfirmCreateRecognitionDialog(e: CustomEvent) {
    e.stopPropagation();

    const isValid = this._checkFormValidity();
    if (!isValid) return;

    let recognition;
    switch (this.type) {
      case RECOGNITION_TYPE.TEXT:
        const selectedBackground = this.backgroundList?.find(
          data => data.id == this.backgroundImgElem.selectedId,
        );
        recognition = {
          message: this.messageElem.value,
          background: selectedBackground,
          textColor: this.textColorElem.selectedColor,
          badge: { ...this.badge },
          images: this.images,
          type: this.type,
          gif: this.gif,
        };
        break;
      case RECOGNITION_TYPE.VIDEO:
        recognition = {
          badge: { ...this.badge },
          type: this.type,
          videoTitle: this.videoTitle.value,
          videoTitleColor: this.videoTitleColorElem?.selectedColor,
          videoURL: this.videoURL,
          videoData: this.videoData,
        };
        break;
      case RECOGNITION_TYPE.AUDIO:
        //TODO: to add new field for audio
        const selectedAudioBackground = this.backgroundList?.find(
          data => data.id == this.audioBackgroundImgElem.selectedId,
        );
        recognition = {
          audioURL: this.audioURL,
          audioTitle: this.audioTitle.value,
          audioTitleColor: this.audioTitleColorElem.selectedColor,
          background: selectedAudioBackground,
          badge: { ...this.badge },
          images: this.images,
          type: this.type,
          gif: this.gif,
        };
        break;
      default:
        break;
    }
    emit(this, 'rnr-click-button-preview', { detail: { ...recognition } });
    this.createRecognitionDialog.hide();
  }

  // related methods with render UI
  private _renderText() {
    return html`
      <div class="row">
        <amityeko-textarea
          required
          id="textarea_message"
          label=${'Message'}
          .placeholder=${'What did your teammate do to deserve a recognition?'}
          .minlength=${1}
          .maxlength=${150}
          ?hasCharacterCounter=${true}
        ></amityeko-textarea>
      </div>

      <div class="row">
        <label for="file">
          <amityeko-tg-caption>Upload</amityeko-tg-caption>
          <input type="file" id="file" accept="image/*" @change=${this._handleFile} />
        </label>
        <amityeko-image-selector
          id="image_selector_background"
          required
          .assets=${this.backgroundList || []}
          .selectedId=${this.backgroundList && this.backgroundList[0]?.id}
          label=${'Background'}
          .visibleCount=${7}
          .scrollStep=${3}
          .itemWidth=${'27px'}
          .itemHeight=${'48px'}
        ></amityeko-image-selector>
      </div>

      <div class="row">
        <amityeko-color-selector
          id="color_selector_text"
          required
          label=${'Text Color'}
          .colors=${colorData}
          .defaultColor=${colorData[0]}
        ></amityeko-color-selector>
      </div>

      <div class="row">
        <amityeko-tg-label-two class="label">Visual</amityeko-tg-label-two>
        <div class="control control--flex-row">
          <rnr-image-layout-content
            id="image_layout_content"
            style=${styleMap({
              '--private-image-layout-display': this.images ? 'block' : 'none',
            })}
            .imageCount=${this.images?.imageCount || 1}
            .currentLayout=${this.images?.layout || 'square'}
            ?isViewOnly=${true}
            .imageKeys=${this.images?.imageKeys || []}
            .width=${'140px'}
            .height=${'140px'}
          >
          </rnr-image-layout-content>
          <div
            id="gif"
            class="gif"
            style=${styleMap({ '--private-gif-display': this.gif ? 'block' : 'none' })}
          ></div>
          ${this.images || this.gif
            ? html`
                <amityeko-button
                  @click=${this._onClickResetVisualButton}
                  variant="primary"
                  .customColor=${'rgba(0, 0, 0, 0.5)'}
                  circle
                >
                  <amityeko-icon .iconSize=${'12px'}>${closeIcon}</amityeko-icon>
                </amityeko-button>
              `
            : html`
                <amityeko-button variant="secondary" @click=${this._onClickAddGifButton}>
                  Add GIF
                </amityeko-button>
                <amityeko-tg-label-two>or</amityeko-tg-label-two>
                <amityeko-button variant="secondary" @click=${this._handleUploadImage}>
                  Upload image
                </amityeko-button>
              `}
        </div>
      </div>
    `;
  }

  private _renderVideoRecord() {
    if (this.isSafari) {
      return html`<amityeko-button variant="secondary" @click=${this._handleRecordVideo}
          >Record video</amityeko-button
        >
        <amityeko-tg-label-two>or</amityeko-tg-label-two>
        <label for="video" class="video__container">
          <input
            type="file"
            id="video"
            accept="video/mp4,video/quicktime"
            @change=${this._handleUploadVideo}
          />
          <amityeko-button variant="secondary" @click=${this._handleUploadVideo}
            >Upload video</amityeko-button
          >
          <div class="upload__video"></div>
        </label>`;
    } else {
      return html`<label for="video" class="video__container">
        <input
          type="file"
          id="video"
          accept="video/mp4,video/quicktime"
          @change=${this._handleUploadVideo}
        />
        <amityeko-button variant="secondary">Upload video</amityeko-button>
        <div class="upload__video"></div>
      </label>`;
    }
  }
  private _renderVideo() {
    return html`
      <div class="row">
        <amityeko-input
          id="video_title"
          required
          clearable
          .label=${'Title'}
          .placeholder=${'Add video title...'}
          .type=${'text'}
          .minlength=${1}
          .maxlength=${150}
          ?hasCharacterCounter=${true}
        ></amityeko-input>
      </div>
      <div class="row">
        <amityeko-color-selector
          id="video_title_color_selector"
          required
          label=${'Text Color'}
          .colors=${colorData}
          .defaultColor=${colorData[0]}
        ></amityeko-color-selector>
      </div>
      <div class="row">
        <amityeko-tg-label-two class="label">Video</amityeko-tg-label-two>
        <div class="control control--flex-row">
          ${this.videoURL
            ? html`<rnr-video .videoURL=${this.videoURL} ?isShowPlayPauseBtn=${true}> </rnr-video>
                <amityeko-button
                  @click=${() => this.discardVideoDialog.show()}
                  variant="primary"
                  .customColor=${'rgba(0, 0, 0, 0.5)'}
                  circle
                >
                  <amityeko-icon .iconSize=${'10px'}>${closeIcon}</amityeko-icon>
                </amityeko-button>`
            : this._renderVideoRecord()}
        </div>
      </div>
    `;
  }

  private _renderAudio() {
    return html`
      <div class="row">
        <amityeko-input
          id="audio_title"
          required
          clearable
          .label=${'Title'}
          .placeholder=${'Add audio title...'}
          .type=${'text'}
          .minlength=${1}
          .maxlength=${50}
          ?hasCharacterCounter=${true}
        ></amityeko-input>
      </div>
      <div class="row">
        <amityeko-tg-label-two class="label">Audio</amityeko-tg-label-two>
        <div class="control control--flex-row">
          ${this.audioURL
            ? html`<rnr-audio
                .audioURL=${this.audioURL}
                ?shouldShowCancelBtn=${true}
                @rnr-cancel-audio=${() => (this.audioURL = undefined)}
              ></rnr-audio>`
            : html`<amityeko-button variant="secondary" @click=${this._handleRecordAudio}
                  >Record audio</amityeko-button
                >
                <amityeko-tg-label-two>or</amityeko-tg-label-two>
                <label for="video">
                  <input
                    type="file"
                    name="file"
                    id="video"
                    accept="audio/mp3"
                    @change=${this._handleUploadAudio}
                  />
                  <amityeko-button variant="secondary">Upload audio</amityeko-button>
                </label>`}
        </div>
      </div>
      <div class="row">
        <label for="file">
          <amityeko-tg-caption>Upload</amityeko-tg-caption>
          <input type="file" id="file" accept="image/*" @change=${this._handleFile} />
        </label>
        <amityeko-image-selector
          id="audio_image_selector_background"
          required
          .assets=${this.backgroundList || []}
          .selectedId=${this.backgroundList && this.backgroundList[0]?.id}
          label=${'Background'}
          .visibleCount=${7}
          .scrollStep=${3}
          .itemWidth=${'27px'}
          .itemHeight=${'48px'}
        ></amityeko-image-selector>
      </div>
      <div class="row">
        <amityeko-color-selector
          id="audio_title_color_selector"
          required
          label=${'Text Color'}
          .colors=${colorData}
          .defaultColor=${colorData[0]}
        ></amityeko-color-selector>
      </div>
      <div class="row">
        <amityeko-tg-label-two class="label">Visual</amityeko-tg-label-two>
        <div class="control control--flex-row">
          <rnr-image-layout-content
            id="audio_image_layout_content"
            style=${styleMap({
              '--private-image-layout-display': this.images ? 'block' : 'none',
            })}
            .imageCount=${this.images?.imageCount || 1}
            .currentLayout=${this.images?.layout || 'square'}
            ?isViewOnly=${true}
            .imageKeys=${this.images?.imageKeys || []}
            .width=${'140px'}
            .height=${'140px'}
          >
          </rnr-image-layout-content>
          <div
            id="gif"
            class="gif"
            style=${styleMap({ '--private-gif-display': this.gif ? 'block' : 'none' })}
          ></div>
          ${this.images || this.gif
            ? html`
                <amityeko-button
                  @click=${this._onClickResetVisualButton}
                  variant="primary"
                  .customColor=${'rgba(0, 0, 0, 0.5)'}
                  circle
                >
                  <amityeko-icon .iconSize=${'12px'}>${closeIcon}</amityeko-icon>
                </amityeko-button>
              `
            : html`
                <amityeko-button variant="secondary" @click=${this._onClickAddGifButton}>
                  Add GIF
                </amityeko-button>
                <amityeko-tg-label-two>or</amityeko-tg-label-two>
                <amityeko-button variant="secondary" @click=${this._handleUploadImage}>
                  Upload image
                </amityeko-button>
              `}
        </div>
      </div>
    `;
  }

  protected render() {
    return html`
      <slot @click=${this._onTriggerClick}></slot>

      <amityeko-image-editor
        id="image_editor"
        @amityeko-image-editor-confirm=${this._handleBackgroundImage}
        @amityeko-image-editor-back=${() => this.createRecognitionDialog.show()}
        @amityeko-image-editor-warning-confirm=${() => this.createRecognitionDialog.show()}
        @amityeko-image-editor-warning-cancel=${() => this.createRecognitionDialog.show()}
      ></amityeko-image-editor>

      <rnr-gif-selector-dialog
        id="gif_selector"
        @rnr-gif-selector-back=${this._onBackGifSelector}
        @rnr-select-gif=${this._onSelectGif}
      ></rnr-gif-selector-dialog>

      <rnr-record-audio
        id="record_audio"
        @rnr-stop-audio-record=${this._onAudioRecordStop}
        @rnr-record-audio-dialog-back=${this._onAudioRecordBack}
      ></rnr-record-audio>

      <rnr-record-video
        id="record_video"
        @rnr-stop-video-record=${this._onVideoRecordStop}
        @rnr-record-video-dialog-back=${this._onVideoRecordBack}
      ></rnr-record-video>

      <amityeko-dialog
        id="create_recognition_dialog"
        .label=${'Celebrate your teammate'}
        ?hasBackButton=${true}
        ?shouldShowFooter=${true}
        ?isDisabled=${this.badgeLimit === 0 || this.isLimitationLoading}
        ?isFooterInfoTextLoading=${this.isLimitationLoading}
        .footerInfoText=${this.footerInfoText}
        ?hasSuffixIconButton=${true}
        .confirmButtonText=${'Preview'}
        @amityeko-confirm=${this._onConfirmCreateRecognitionDialog}
        @amityeko-back=${this._onDialogBack}
        @amityeko-cancel=${this.reset}
      >
        <div class="content">
          <div class="row">
            <amityeko-tg-label-two class="label">Type</amityeko-tg-label-two>
            <amityeko-chips-selection
              class="control control--flex-row"
              clickable
              .options=${chipList}
              @amityeko-select=${this._onSelectType}
              .selectedId=${this.type}
            ></amityeko-chips-selection>
          </div>
          <div class="divider"></div>
          ${this.type === RECOGNITION_TYPE.TEXT ? this._renderText() : null}
          ${this.type === RECOGNITION_TYPE.VIDEO ? this._renderVideo() : null}
          ${this.type === RECOGNITION_TYPE.AUDIO ? this._renderAudio() : null}
        </div>
      </amityeko-dialog>

      <!--Discard Changes Dialog -->
      <amityeko-dialog
        id="discard_changes_dialog"
        .variant=${'danger'}
        ?hasIconAsLabel=${true}
        .cancelButtonText=${'Continue editing'}
        .confirmButtonText=${'Discard changes'}
        @amityeko-confirm=${this._onDiscardChanges}
        @amityeko-cancel=${this._onContinueEditing}
      >
        <div class="quit-dialog-content">
          <amityeko-tg-title>Discard changes?</amityeko-tg-title>
          <amityeko-tg-body-two>
            You will lose any changes you’ve made if you select other tabs.
          </amityeko-tg-body-two>
        </div>
      </amityeko-dialog>

      <!--Discard Video Dialog -->
      <amityeko-dialog
        id="discard_video_dialog"
        .variant=${'danger'}
        ?hasIconAsLabel=${true}
        .cancelButtonText=${'Cancel'}
        .confirmButtonText=${'Discard'}
        @amityeko-confirm=${this._onDiscardVideo}
        @amityeko-cancel=${this._onContinueVideo}
      >
        <div class="quit-dialog-content">
          <amityeko-tg-title>Discard video?</amityeko-tg-title>
          <amityeko-tg-body-two>You can upload other videos later.</amityeko-tg-body-two>
        </div>
      </amityeko-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rnr-create-recognition': RnRCreateRecognition;
  }
}
